/**
 * Cart remove (trash) placement:
 * - Subscription: next to frequency / variant text (right-aligned)
 * - One-time: same row as quantity, right-aligned across from it
 * No core Horizon markup changes.
 */

/**
 * @param {HTMLElement} variants
 * @returns {boolean}
 */
function hasSellingPlanName(variants) {
  return Array.from(variants.children).some(
    (el) => el instanceof HTMLParagraphElement && !el.classList.contains('cart-items__remove')
  );
}

/**
 * @param {HTMLElement} row
 * @returns {boolean}
 */
function isSubscriptionRow(row) {
  const variants = row.querySelector('.cart-items__variants-wrapper');
  return variants instanceof HTMLElement && hasSellingPlanName(variants);
}

/**
 * @param {HTMLElement} row
 * @returns {HTMLElement | null}
 */
function findRemoveButton(row) {
  const remove = row.querySelector(
    '.cart-items__quantity .cart-items__remove, .cart-items__quantity-controls .cart-items__remove, .cart-items__variants-wrapper > .cart-items__remove, .cart-items__unit-price-wrapper > .cart-items__remove, .cart-items__product-info > .cart-items__remove'
  );
  return remove instanceof HTMLElement ? remove : null;
}

/**
 * @param {ParentNode} [root]
 */
function repositionCartRemoveButtons(root = document) {
  const rows = root.querySelectorAll('.cart-items__table-row');

  for (const row of rows) {
    if (!(row instanceof HTMLElement)) continue;

    const remove = findRemoveButton(row);
    if (!remove) continue;

    const subscription = isSubscriptionRow(row);

    if (subscription) {
      const variants = row.querySelector('.cart-items__variants-wrapper');
      if (variants instanceof HTMLElement && remove.parentElement !== variants) {
        variants.appendChild(remove);
      }
      row.classList.add('cart-items__table-row--subscription');
      row.classList.remove('cart-items__table-row--onetime-remove');
    } else {
      const controls = row.querySelector('.cart-items__quantity-controls');
      if (controls instanceof HTMLElement && remove.parentElement !== controls) {
        controls.appendChild(remove);
      }
      row.classList.add('cart-items__table-row--onetime-remove');
      row.classList.remove('cart-items__table-row--subscription');
    }

    row.classList.add('cart-items__table-row--remove-aligned');
  }
}

function scheduleReposition() {
  requestAnimationFrame(() => repositionCartRemoveButtons());
}

function init() {
  repositionCartRemoveButtons();

  document.addEventListener('cart:update', scheduleReposition);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList' && mutation.addedNodes.length) {
        scheduleReposition();
        break;
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
